import React, { FC, memo, ReactNode, useState, useCallback } from "react";
import {
  DropdownMenuWrapper,
  DropdownMenuBody,
  DropdownMenuItemStyled,
  TriggerWrapper,
} from "./DropdownMenu.styles";

interface DropdownMenuProps {
  trigger: ReactNode;
  children: ReactNode;
  disabled?: boolean;
  place?: "below" | "above" | "left" | "right";
  preferPlace?: "below" | "above" | "left" | "right" | "row" | "column";
}

export interface DropdownMenuItemProps {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}

export const DropdownMenuItem: FC<DropdownMenuItemProps> = memo(
  ({ children, onClick, disabled = false }) => {
    return (
      <DropdownMenuItemStyled onClick={onClick} disabled={disabled}>
        {children}
      </DropdownMenuItemStyled>
    );
  },
);

const DropdownMenu: FC<DropdownMenuProps> & { Item: typeof DropdownMenuItem } =
  ({ trigger, children, disabled = false, place, preferPlace = "below" }) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleToggle = useCallback(() => {
      if (!disabled) {
        setIsOpen((prev) => !prev);
      }
    }, [disabled]);

    const handleClose = useCallback(() => {
      setIsOpen(false);
    }, []);

    const handleItemClick = useCallback(
      (onClick?: () => void) => {
        return () => {
          onClick?.();
          handleClose();
        };
      },
      [handleClose],
    );

    const renderChildren = () => {
      return React.Children.map(children, (child) => {
        if (React.isValidElement<DropdownMenuItemProps>(child)) {
          return React.cloneElement(child, {
            onClick: handleItemClick(child.props.onClick),
          });
        }
        return child;
      });
    };

    return (
      <DropdownMenuWrapper
        isOpen={isOpen}
        body={<DropdownMenuBody>{renderChildren()}</DropdownMenuBody>}
        place={place}
        preferPlace={preferPlace}
        onOuterAction={handleClose}
      >
        <TriggerWrapper onClick={handleToggle}>{trigger}</TriggerWrapper>
      </DropdownMenuWrapper>
    );
  };

DropdownMenu.Item = DropdownMenuItem;

export default DropdownMenu;
